import firebase, { boardsCollection } from "../firebase";
import { v4 as uuidv4 } from "uuid";
import { prepareFirebaseUpdatePayload } from "./serviceUtil";
import { getCurrentUserId, logError } from "./../util/common";

class CardService {
  async create(documentId, columnId, cardPayload) {
    const boardDocRef = boardsCollection.doc(documentId);

    // Every update, make sure card exist in db where it is supposed to be before updating.
    // If it no longer exist, we don't update.
    firebase
      .firestore()
      .runTransaction((transaction) => {
        return transaction.get(boardDocRef).then((boardDoc) => {
          if (!boardDoc.exists) {
            throw "Document does not exist!";
          }
          const dbBoardJSON = boardDoc.data();

          if (dbBoardJSON.columns[columnId].cards) {
            let currentMaxIndex;
            if (Object.keys(dbBoardJSON.columns[columnId].cards).length === 0) {
              currentMaxIndex = -1;
            } else {
              currentMaxIndex = Math.max(
                ...(Object.values(dbBoardJSON.columns[columnId].cards).map(
                  (cardObject) => cardObject.index
                ) ?? [])
              );
            }

            const update = {};
            update[`columns.${columnId}.cards.${uuidv4()}`] = {
              // Default values
              content: "Add contents here...",
              index: currentMaxIndex + 1,
              tags: [],
              votes: {
                awesome: [],
                clap: [],
                like: [],
                love: [],
                insightful: [],
              },
              created_at: firebase.firestore.FieldValue.serverTimestamp(),
              created_by: getCurrentUserId(),
              updated_at: firebase.firestore.FieldValue.serverTimestamp(),
              updated_by: getCurrentUserId(),
              // User Values
              ...cardPayload,
            };
            return transaction.update(boardDocRef, update);
          } else {
            throw "Update failed. The cards have changed since your last update";
          }
        });
      })
      .then(() => {
        console.log("Create Card Transaction successfully committed!");
      })
      .catch((error) => {
        console.error("Create Card Transaction failed: ", error);
        logError(error);
      });
  }

  async updateWithCustomPayload(documentId, updatePayload) {
    return boardsCollection.doc(documentId).update(updatePayload);
  }

  async update(documentId, columnId, cardId, fieldsToUpdate) {
    const boardDocRef = boardsCollection.doc(documentId);

    // Every update, make sure card exist in db where it is supposed to be before updating.
    // If it no longer exist, we don't update.
    firebase
      .firestore()
      .runTransaction((transaction) => {
        return transaction.get(boardDocRef).then((boardDoc) => {
          if (!boardDoc.exists) {
            throw "Document does not exist!";
          }
          const dbBoardJSON = boardDoc.data();

          // if card no longer exist where it was, don't update anything to avoid creating the card again.
          if (dbBoardJSON.columns[columnId].cards[cardId]) {
            // step two, prepare payload of fields to update for this card
            const updateObject = prepareFirebaseUpdatePayload(
              `columns.${columnId}.cards.${cardId}`,
              {
                ...fieldsToUpdate,
                updated_at: firebase.firestore.FieldValue.serverTimestamp(),
                updated_by: getCurrentUserId(),
              }
            );
            return transaction.update(boardDocRef, updateObject);
          } else {
            throw "Update failed. The cards have changed since your last update";
          }
        });
      })
      .then(() => {
        console.log("Update Transaction successfully committed!");
      })
      .catch((error) => {
        console.error("Update Transaction failed: ", error);
        logError(error);
      });
  }

  async upvoteCard(documentId, columnId, cardId, votingCategoryFieldName) {
    const boardDocRef = boardsCollection.doc(documentId);

    // Every update, make sure card exist in db where it is supposed to be before updating.
    // If it no longer exist, we don't update.
    firebase
      .firestore()
      .runTransaction((transaction) => {
        return transaction.get(boardDocRef).then((boardDoc) => {
          if (!boardDoc.exists) {
            throw "Document does not exist!";
          }
          const dbBoardJSON = boardDoc.data();

          // if card no longer exist where it was, don't update anything to avoid creating the card again.
          if (
            dbBoardJSON.columns[columnId].cards[cardId].votes[
              votingCategoryFieldName
            ]
          ) {
            const currentVotes = new Set(
              dbBoardJSON.columns[columnId].cards[cardId].votes[
                votingCategoryFieldName
              ] ?? []
            );
            if (!currentVotes.has(getCurrentUserId())) {
              // add extra vote
              currentVotes.add(getCurrentUserId());
            } else {
              // remove user's previous vote (un-vote)
              currentVotes.delete(getCurrentUserId());
            }
            // update the card info
            const update = {};
            update[
              `columns.${columnId}.cards.${cardId}.votes.${votingCategoryFieldName}`
            ] = Array.from(currentVotes);
            return transaction.update(boardDocRef, update);
          } else {
            throw "Update failed. The cards have changed since your last update";
          }
        });
      })
      .then(() => {
        console.log("Voting Transaction successful");
      })
      .catch((error) => {
        console.error("Voting Transaction failed: ", error);
        logError(error);
      });
  }

  async moveCardToAnotherColumn(
    documentId,
    cardId,
    source,
    destination,
    newColumns
  ) {
    const update = {};
    const boardDocRef = boardsCollection.doc(documentId);
    // Startegy: Read board json from DB and use it a source of truth to make the updates. At
    // every step check if values are null, it means local data don't match db one and we can show error.
    firebase
      .firestore()
      .runTransaction((transaction) => {
        return transaction.get(boardDocRef).then((boardDoc) => {
          if (!boardDoc.exists) {
            throw "Document does not exist!";
          }
          const dbBoardJSON = boardDoc.data();

          const cardDBJSON =
            dbBoardJSON.columns[source.droppableId].cards[cardId];
          if (!cardDBJSON) {
            throw "Update failed. The cards have changed since your last update";
          }

          // 1- add card to destination column
          if (dbBoardJSON.columns[source.droppableId].cards[cardId]) {
            update[`columns.${destination.droppableId}.cards.${cardId}`] = {
              ...cardDBJSON,
              index: destination.index,
              updated_at: firebase.firestore.FieldValue.serverTimestamp(),
              updated_by: getCurrentUserId(),
            };
          } else {
            throw "Update failed. The cards have changed since your last update";
          }

          // 2- re-index source column cards
          newColumns[source.droppableId].map((card, index) => {
            // check if card still in that column in the db
            if (dbBoardJSON.columns[source.droppableId].cards[card.cardId]) {
              update[
                `columns.${source.droppableId}.cards.${card.cardId}.index`
              ] = index;
            } else {
              throw "Update failed. The cards have changed since your last update";
            }
          });

          // 3- re-index destination column cards
          newColumns[destination.droppableId].forEach((card, index) => {
            if (card.cardId !== cardId) {
              if (
                dbBoardJSON.columns[destination.droppableId].cards[card.cardId]
              ) {
                // we might need to exclude the new card from indexing here since we already added it at the top.
                update[
                  `columns.${destination.droppableId}.cards.${card.cardId}.index`
                ] = index;
              } else {
                throw "Update failed. The cards have changed since your last update";
              }
            }
          });

          // 4- delete card from original column
          if (!!dbBoardJSON.columns[source.droppableId].cards[cardId]) {
            update[
              `columns.${source.droppableId}.cards.${cardId}`
            ] = firebase.firestore.FieldValue.delete();
          } else {
            throw "Update failed. The cards have changed since your last update";
          }
          return transaction.update(boardDocRef, update);
        });
      })
      .then(() => {
        console.log("Card transfer Transaction successfully committed!");
      })
      .catch((error) => {
        console.error("Card transfer Transaction failed: ", error);
        logError(error);
      });
  }

  async delete(documentId, columnId, cardId) {
    const update = {};
    update[
      `columns.${columnId}.cards.${cardId}`
    ] = firebase.firestore.FieldValue.delete();
    return boardsCollection.doc(documentId).update(update);
  }
}

export default new CardService();
